import type { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { removeState, setState } from 'store/entities/navigation/actions';
import type { NavigationStateStore } from 'store/entities/navigation/types';
import { SLICER_KEYS } from 'types/services';

const RECORD_RETURN_TO_KEY_PREFIX = 'record-return-to';

interface StateWithNavigation {
  [SLICER_KEYS.NAVIGATION_STATE]: NavigationStateStore;
}

interface RecordReturnToStoreApi {
  getState: () => StateWithNavigation;
  dispatch: Dispatch<AnyAction>;
}

let storeApi: RecordReturnToStoreApi | null = null;

// Injected from store/index.ts instead of importing 'store' to keep this module a leaf — importing the store would close a cycle through the store init graph.
export const initRecordReturnTo = (api: RecordReturnToStoreApi): void => {
  storeApi = api;
};

const getRecordReturnToKey = (recordDetailPath: string) => `${RECORD_RETURN_TO_KEY_PREFIX}:${recordDetailPath}`;

const isRecordReturnToData = (data: unknown): data is { originPath: string } =>
  typeof data === 'object' && data !== null && 'originPath' in data && typeof data.originPath === 'string';

export const isRecordDetailPath = (path: string): boolean => /\/r\/[^/]+\/detail$/.test(path);

export const getRecordReturnTo = (recordDetailPath: string): string | undefined => {
  const navigationStates = storeApi?.getState()[SLICER_KEYS.NAVIGATION_STATE];
  const entry = navigationStates?.[getRecordReturnToKey(recordDetailPath)];

  if (!entry || !isRecordReturnToData(entry.data)) {
    return undefined;
  }

  return entry.data.originPath;
};

export const rememberRecordReturnTo = (recordDetailPath: string, originPath: string): void => {
  storeApi?.dispatch(setState({ key: getRecordReturnToKey(recordDetailPath), data: { originPath } }));
};

export const consumeRecordReturnTo = (recordDetailPath: string): string | undefined => {
  const originPath = getRecordReturnTo(recordDetailPath);

  if (originPath) {
    storeApi?.dispatch(removeState(getRecordReturnToKey(recordDetailPath)));
  }

  return originPath;
};

export const forgetRecordReturnTo = (recordDetailPath: string): void => {
  storeApi?.dispatch(removeState(getRecordReturnToKey(recordDetailPath)));
};

export const trackRecordReturnToForRedirect = (redirectTarget: string): void => {
  const currentPath = window.location.pathname;

  if (currentPath === redirectTarget || !isRecordDetailPath(currentPath) || !isRecordDetailPath(redirectTarget)) {
    return;
  }

  if (getRecordReturnTo(currentPath) === redirectTarget) {
    consumeRecordReturnTo(currentPath);
    return;
  }

  rememberRecordReturnTo(redirectTarget, currentPath);
};

export const trackRecordReturnToForPath = (currentPath: string, targetPath: string): void => {
  if (currentPath === targetPath || !isRecordDetailPath(currentPath) || !isRecordDetailPath(targetPath)) {
    return;
  }

  if (getRecordReturnTo(currentPath) === targetPath) {
    consumeRecordReturnTo(currentPath);
    return;
  }

  rememberRecordReturnTo(targetPath, currentPath);
};
